/**
 * TechnicianProfile Page
 *
 * Mobile-first profile and settings page for technicians.
 * Features profile editing, avatar upload, password change, and sign out.
 *
 * @module pages/technician/TechnicianProfile
 */

import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import {
  Container,
  Card,
  Form,
  Button,
  Badge,
  Modal,
  ListGroup,
  ProgressBar,
  Spinner,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';
import { authService, supabase } from '../../services/supabase';
import './TechnicianPages.css';

const TechnicianProfile = () => {
  const { user, profile, updateProfile, signOut } = useAuth();

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
      });
    }
  }, [profile]);

  // Avatar state
  const fileInputRef = useRef(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || null);

  useEffect(() => {
    if (profile?.avatar_url) setAvatarUrl(profile.avatar_url);
  }, [profile?.avatar_url]);

  const getInitials = useCallback(() => {
    const name = profile?.full_name || '';
    return name
      .split(' ')
      .map((n) => n[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }, [profile?.full_name]);

  // Password state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwords, setPasswords] = useState({ newPassword: '', confirmPassword: '' });

  // Sign out state
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  const passwordStrength = useMemo(() => {
    const pw = passwords.newPassword;
    if (!pw) return { score: 0, label: '', variant: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (score <= 1) return { score: 25, label: 'Debil', variant: 'danger' };
    if (score === 2) return { score: 50, label: 'Regular', variant: 'warning' };
    if (score === 3) return { score: 75, label: 'Buena', variant: 'info' };
    return { score: 100, label: 'Fuerte', variant: 'success' };
  }, [passwords.newPassword]);

  const passwordErrors = useMemo(() => {
    const pw = passwords.newPassword;
    const errors = [];
    if (pw && pw.length < 8) errors.push('Minimo 8 caracteres');
    if (pw && !/[A-Z]/.test(pw)) errors.push('Una letra mayuscula');
    if (pw && !/[a-z]/.test(pw)) errors.push('Una letra minuscula');
    if (pw && !/[0-9]/.test(pw)) errors.push('Un numero');
    return errors;
  }, [passwords.newPassword]);

  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.full_name.trim()) {
      toast.error('El nombre completo es requerido');
      return;
    }
    setIsSaving(true);
    try {
      const { success, error } = await updateProfile(formData);
      if (success) {
        toast.success('Perfil actualizado');
        setIsEditing(false);
      } else {
        throw error;
      }
    } catch (error) {
      toast.error(error?.message || 'Error al actualizar el perfil');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      full_name: profile?.full_name || '',
      phone: profile?.phone || '',
    });
    setIsEditing(false);
  };

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imagenes JPG, PNG o WebP');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen no debe superar 2MB');
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const ext = file.name.split('.').pop();
      const filePath = `${user.id}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { cacheControl: '3600', upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      const { success, error } = await updateProfile({ avatar_url: publicUrl });
      if (!success) throw error;

      setAvatarUrl(publicUrl);
      toast.success('Foto de perfil actualizada');
    } catch (err) {
      console.error('Avatar upload error:', err);
      toast.error('Error al subir la imagen');
    } finally {
      setIsUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleChangePassword = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('Las contrasenas no coinciden');
      return;
    }
    if (passwordErrors.length > 0) {
      toast.error('La contrasena no cumple los requisitos');
      return;
    }
    setIsChangingPassword(true);
    try {
      const { success, error } = await authService.updatePassword(passwords.newPassword);
      if (success) {
        toast.success('Contrasena actualizada');
        setShowPasswordModal(false);
        setPasswords({ newPassword: '', confirmPassword: '' });
      } else {
        throw error;
      }
    } catch (err) {
      toast.error(err?.message || 'Error al cambiar la contrasena');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Sesion cerrada');
    } catch (err) {
      toast.error('Error al cerrar la sesion');
    }
    setShowSignOutConfirm(false);
  };

  if (!profile) {
    return (
      <Container fluid className="technician-page">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="technician-page p-0">
      {/* Header */}
      <div className="page-header px-3 pt-3 pb-2">
        <h1 className="page-title">Mi Perfil</h1>
        <p className="page-subtitle">Informacion y configuracion</p>
      </div>

      <div className="px-3 pb-3">
        {/* Avatar Card */}
        <Card className="dashboard-card mb-3">
          <Card.Body className="text-center py-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAvatarUpload}
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
            />
            <div
              onClick={handleAvatarClick}
              className="hex-avatar hex-avatar-xl"
              style={{
                margin: '0 auto',
                cursor: 'pointer',
                position: 'relative',
                background: avatarUrl ? 'transparent' : '#0d6efd',
              }}
              title="Cambiar foto de perfil"
            >
              {isUploadingAvatar ? (
                <Spinner animation="border" variant="light" size="sm" />
              ) : avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Avatar"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
                  {getInitials()}
                </span>
              )}
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  padding: '3px 0',
                  opacity: isUploadingAvatar ? 0 : 1,
                }}
              >
                <i className="bi bi-camera text-white" style={{ fontSize: '0.7rem' }}></i>
              </div>
            </div>
            <h4 className="mt-3 mb-1" style={{ fontWeight: 600 }}>
              {profile.full_name}
            </h4>
            <p className="text-muted mb-1" style={{ fontSize: '0.9rem' }}>
              {user?.email}
            </p>
            <Badge bg="primary">Tecnico</Badge>
          </Card.Body>
        </Card>

        {/* Personal Info Card */}
        <Card className="dashboard-card mb-3">
          <Card.Header
            className="d-flex justify-content-between align-items-center"
            style={{ background: '#ffffff', borderBottom: '1px solid #e9ecef' }}
          >
            <h6 className="mb-0" style={{ color: '#212529' }}>
              <i className="bi bi-person me-2 text-primary"></i>
              Informacion Personal
            </h6>
            {!isEditing && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                <i className="bi bi-pencil me-1"></i>Editar
              </Button>
            )}
          </Card.Header>

          {!isEditing ? (
            <ListGroup variant="flush">
              <ListGroup.Item className="d-flex justify-content-between">
                <span className="text-muted">Nombre</span>
                <span style={{ color: '#212529', fontWeight: 500 }}>
                  {profile.full_name || 'No especificado'}
                </span>
              </ListGroup.Item>
              <ListGroup.Item className="d-flex justify-content-between">
                <span className="text-muted">Email</span>
                <span style={{ color: '#212529', fontWeight: 500, fontSize: '0.9rem' }}>
                  {user?.email}
                </span>
              </ListGroup.Item>
              <ListGroup.Item className="d-flex justify-content-between">
                <span className="text-muted">Telefono</span>
                <span style={{ color: '#212529', fontWeight: 500 }}>
                  {profile.phone || 'No registrado'}
                </span>
              </ListGroup.Item>
            </ListGroup>
          ) : (
            <Card.Body>
              <Form onSubmit={handleSave}>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted">Nombre Completo *</Form.Label>
                  <Form.Control
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted">Telefono</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+506 XXXX-XXXX"
                  />
                </Form.Group>
                <div className="d-flex gap-2">
                  <Button type="submit" variant="primary" size="sm" disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-1"></i>Guardar
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Cancelar
                  </Button>
                </div>
              </Form>
            </Card.Body>
          )}
        </Card>

        {/* Security Card */}
        <Card className="dashboard-card mb-3">
          <Card.Header style={{ background: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
            <h6 className="mb-0" style={{ color: '#212529' }}>
              <i className="bi bi-shield-lock me-2 text-primary"></i>
              Seguridad
            </h6>
          </Card.Header>
          <ListGroup variant="flush">
            <ListGroup.Item
              action
              onClick={() => setShowPasswordModal(true)}
              className="d-flex justify-content-between align-items-center"
            >
              <span style={{ color: '#495057' }}>
                <i className="bi bi-key me-2"></i>
                Cambiar Contrasena
              </span>
              <i className="bi bi-chevron-right text-muted"></i>
            </ListGroup.Item>
            <ListGroup.Item
              action
              onClick={() => setShowSignOutConfirm(true)}
              className="d-flex justify-content-between align-items-center"
            >
              <span style={{ color: '#dc3545' }}>
                <i className="bi bi-box-arrow-right me-2"></i>
                Cerrar Sesion
              </span>
              <i className="bi bi-chevron-right text-muted"></i>
            </ListGroup.Item>
          </ListGroup>
        </Card>

        {/* Account Info */}
        <Card className="dashboard-card mb-3">
          <Card.Header style={{ background: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
            <h6 className="mb-0" style={{ color: '#212529' }}>
              <i className="bi bi-info-circle me-2 text-primary"></i>
              Cuenta
            </h6>
          </Card.Header>
          <ListGroup variant="flush">
            <ListGroup.Item className="d-flex justify-content-between">
              <span className="text-muted small">Ultimo acceso</span>
              <span className="small" style={{ color: '#212529' }}>
                {user?.last_sign_in_at
                  ? new Date(user.last_sign_in_at).toLocaleDateString('es-CR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : 'No disponible'}
              </span>
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </div>

      {/* Change Password Modal */}
      <Modal
        show={showPasswordModal}
        onHide={() => {
          setShowPasswordModal(false);
          setPasswords({ newPassword: '', confirmPassword: '' });
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="h6">
            <i className="bi bi-key me-2"></i>
            Cambiar Contrasena
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small">Nueva Contrasena</Form.Label>
            <Form.Control
              type="password"
              value={passwords.newPassword}
              onChange={(e) =>
                setPasswords((prev) => ({ ...prev, newPassword: e.target.value }))
              }
              placeholder="Ingresa tu nueva contrasena"
            />
          </Form.Group>

          {passwords.newPassword && (
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <small className="text-muted">Seguridad:</small>
                <small className={`text-${passwordStrength.variant}`}>
                  {passwordStrength.label}
                </small>
              </div>
              <ProgressBar
                now={passwordStrength.score}
                variant={passwordStrength.variant}
                style={{ height: '5px' }}
              />
              {passwordErrors.length > 0 && (
                <div className="mt-2">
                  {passwordErrors.map((err, i) => (
                    <small key={i} className="d-block text-danger">
                      <i className="bi bi-x-circle me-1"></i>
                      {err}
                    </small>
                  ))}
                </div>
              )}
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="small">Confirmar Contrasena</Form.Label>
            <Form.Control
              type="password"
              value={passwords.confirmPassword}
              onChange={(e) =>
                setPasswords((prev) => ({ ...prev, confirmPassword: e.target.value }))
              }
              placeholder="Confirma tu nueva contrasena"
              isInvalid={
                passwords.confirmPassword &&
                passwords.newPassword !== passwords.confirmPassword
              }
            />
            {passwords.confirmPassword &&
              passwords.newPassword !== passwords.confirmPassword && (
                <Form.Control.Feedback type="invalid">
                  Las contrasenas no coinciden
                </Form.Control.Feedback>
              )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowPasswordModal(false);
              setPasswords({ newPassword: '', confirmPassword: '' });
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleChangePassword}
            disabled={
              isChangingPassword ||
              !passwords.newPassword ||
              !passwords.confirmPassword ||
              passwordErrors.length > 0 ||
              passwords.newPassword !== passwords.confirmPassword
            }
          >
            {isChangingPassword ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Cambiando...
              </>
            ) : (
              'Cambiar Contrasena'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Sign Out Confirmation Modal */}
      <Modal
        show={showSignOutConfirm}
        onHide={() => setShowSignOutConfirm(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h6">Cerrar Sesion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0 small">
            Se cerrara tu sesion actual. Tendras que iniciar sesion de nuevo.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowSignOutConfirm(false)}
          >
            Cancelar
          </Button>
          <Button variant="danger" size="sm" onClick={handleSignOut}>
            <i className="bi bi-box-arrow-right me-1"></i>
            Cerrar Sesion
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TechnicianProfile;
